// Static stand-in for framer-motion.
//
// The EasiLinked design system allows only 150ms color changes on hover: nothing fades in,
// slides, lifts or moves with scrolling (EP-001 v1.1, D19). Components still use the familiar
// `motion.div` / `AnimatePresence` API, but this module renders plain elements and drops every
// animation prop, so content is visible immediately and the animation library is not shipped.
import { createElement, forwardRef } from "react";

const ANIMATION_PROPS = new Set([
    "initial",
    "animate",
    "exit",
    "transition",
    "variants",
    "custom",
    "whileHover",
    "whileTap",
    "whileFocus",
    "whileDrag",
    "whileInView",
    "viewport",
    "layout",
    "layoutId",
    "layoutScroll",
    "drag",
    "dragConstraints",
    "dragElastic",
    "dragMomentum",
    "onAnimationStart",
    "onAnimationComplete",
    "onUpdate",
    "onHoverStart",
    "onHoverEnd",
    "onTapStart",
    "onTap",
    "onTapCancel",
    "onViewportEnter",
    "onViewportLeave",
]);

// A motion value from useScroll/useTransform; it never changes here.
const staticValue = (value) => ({ get: () => value, set: () => {}, on: () => () => {}, isStatic: true });
const isMotionValue = (v) => v && typeof v === "object" && v.isStatic === true;

const cache = new Map();
const staticComponent = (tag) => {
    if (!cache.has(tag)) {
        const Component = forwardRef(({ style, ...props }, ref) => {
            const clean = {};
            for (const key of Object.keys(props)) {
                if (!ANIMATION_PROPS.has(key)) clean[key] = props[key];
            }
            let cleanStyle;
            if (style) {
                cleanStyle = {};
                for (const [key, value] of Object.entries(style)) {
                    if (!isMotionValue(value)) cleanStyle[key] = value;
                }
            }
            return createElement(tag, { ...clean, style: cleanStyle, ref });
        });
        Component.displayName = `static.${typeof tag === "string" ? tag : "component"}`;
        cache.set(tag, Component);
    }
    return cache.get(tag);
};

export const motion = new Proxy({}, { get: (_, tag) => staticComponent(tag) });

export const AnimatePresence = ({ children }) => children ?? null;

export const MotionConfig = ({ children }) => children ?? null;

export const useScroll = () => ({
    scrollX: staticValue(0),
    scrollY: staticValue(0),
    scrollXProgress: staticValue(0),
    scrollYProgress: staticValue(0),
});

export const useTransform = (_value, _input, output) =>
    staticValue(Array.isArray(output) ? output[0] : 0);
